import { computed, reactive, watch } from "vue";
import type { InjectionKey } from "vue";
import { send, notifyError } from "./request";
import type { Request, KscResponseModel } from "./request";
import { ENABLE_CHECK_PRODUCT_PERMISSION } from "@kce2/feature_flags";
import { sortByName } from "../components/BaseInfoSelect/sort";
import {
  type Region as OriginalRegion,
  type AZInfo,
  type GetResponseResponse,
} from "@kce2/common/api/commons";
import { areaMap } from "../components/RegionButtonGroup/utils";

export interface Region {
  InnerCode: string;
  RegionCode: string;
  RegionName: string;
  RegionEnName: string;
  AZList: AZInfo[];
  Active: boolean;
  RegionId: number;
  AreaCode: string;
  AreaName: string;
  AreaEnName: string;
}

export type BaseInfo = {
  isPublicCloud: boolean;
  checkingProductPermission: boolean;
  loadingRegions: boolean;
  regionReady: boolean;
  region: string;
  regionFull: Region;
  serviceIsOpened: boolean;
  regions: Region[];
};

let _namespaceSortMethod = sortByName;

export const BaseInfoContextKey: InjectionKey<BaseInfo> = Symbol("BaseInfoContextKey");

export const baseInfo = reactive<BaseInfo>({
  isPublicCloud: __PUBLIC_CLOUD__,
  regions: [] as Region[],
  region: "",
  regionFull: {} as Region,
  // When permission check is disabled, default to having permission
  // When enabled, wait for checkProductPermission() to set the value
  serviceIsOpened: !ENABLE_CHECK_PRODUCT_PERMISSION,
  loadingRegions: false,
  regionReady: false,
  // When permission check is enabled, start with checking state to prevent
  // showing "no permission" error before the API call completes
  checkingProductPermission: ENABLE_CHECK_PRODUCT_PERMISSION,
});

// Auto-derive regionFull when region or regions changes
// Runs once when module is imported
watch(
  [() => baseInfo.region, () => baseInfo.regions],
  ([region, regions]) => {
    if (regions.length > 0) {
      const matched = regions.find(r => r.RegionCode === region)
      if (matched) {
        baseInfo.regionFull = matched
      }
      // No match: keep current regionFull (no fallback)
    }
  }
)

export const kFallbackRegion = import.meta.env.VITE_REGION ?? "";

export function useBaseInfoRegion() {
  return {
    region: computed(() => baseInfo.region ?? kFallbackRegion),
    regionFull: computed(() => baseInfo.regionFull),
  };
}

export function getAreaFromRegion(v?: string) {
  return (v?.split("-")[0] || "").toUpperCase();
}

export function normalizeRegion(item: OriginalRegion): Region {
  const areaCode = item.areaCode ?? getAreaFromRegion(item.RegionCode);
  const areaName = item.areaName ?? areaMap(areaCode);
  return {
    RegionCode: item.RegionCode,
    InnerCode: item.InnerCode,
    RegionId: item.regionId,
    RegionName: item.RegionName,
    RegionEnName: item.RegionEnName,
    Active: item.active,
    AZList: item.azList,
    AreaCode: areaCode,
    AreaName: areaName,
    AreaEnName: item.areaEnName ?? "",
    // isVpc: item.RegionCode == 'cn-beijing-5' ? 0 : +item.isVpc,
  };
}

export async function checkProductPermission() {
  if (!ENABLE_CHECK_PRODUCT_PERMISSION) {
    baseInfo.serviceIsOpened = true;
    return Promise.resolve(true);
  }

  const check = getServices().checkProductPermission;
  if (!check) {
    console.warn("Please set `checkProductPermission` before used.");
    return;
  }

  baseInfo.checkingProductPermission = true;
  try {
    const openRes = await send(check({ Region: baseInfo.region }));
    baseInfo.serviceIsOpened = openRes.data.Data ?? false;
  } catch (e) {
    baseInfo.serviceIsOpened = false;
    notifyError("", e);
  } finally {
    baseInfo.checkingProductPermission = false;
  }
}

export type Services = {
  getRegions: (defaultRegions?: string) => Request<GetResponseResponse>;
  checkProductPermission: (args: { Region: string }) => Request<KscResponseModel<boolean>>;
};

const __services__: Partial<Services> = {};

export function getServices(): Services {
  return {
    getRegions(defaultRegions?: string) {
      const getRegions = __services__.getRegions;
      if (!getRegions) {
        throw new Error("please set `getRegions` before used");
      }
      return getRegions(defaultRegions);
    },
    checkProductPermission(args: { Region: string }) {
      const checkProductPermission = __services__.checkProductPermission;
      if (!checkProductPermission) {
        throw new Error("please set `checkProductPermission` before used");
      }
      return checkProductPermission(args);
    },
  };
}

export function setServices(services: typeof __services__) {
  Object.assign(__services__, services);
}

export function getRegionLabelText(region: Region) {
  // TODO: support i18n
  return region.RegionName;
}

export type SortFunc = typeof sortByName;

export function setNamespaceSortMethod(method: SortFunc) {
  _namespaceSortMethod = method;
}

// export function setInterceptor(fn: () => boolean, type: string) { }

const _cb: ((info: BaseInfo) => void)[] = [];

export function emitBaseInfoChanged() {
  _cb.forEach(cb => {
    cb(baseInfo);
  });
}

export function registerBaseInfoChanged(cb: (info: BaseInfo) => void) {
  _cb.push(cb);
  return () => {
    const idx = _cb.indexOf(cb);
    if (idx < 0) {
      return;
    }
    _cb.splice(idx, 1);
  };
}
